import { useEffect, useState } from 'react'
import { Inbox, AlertTriangle, Plus, Search } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useTickets } from '@/queries/tickets'
import { EmptyState } from '@/components/ui/empty-state'
import { ListSkeleton } from '@/components/ui/skeleton'
import { TicketRow } from './TicketRow'
import { ViewingAsBanner } from '@/features/view-as/ViewingAsBanner'
import { priorityLabels } from '@/lib/status'
import type { Ticket, TicketPriority } from '@/types/ticket'

// The customer's own words for the derived statuses; each maps to the API's
// derivedStatus values it covers (a user reply is with us, so it is Open).
const STATUSES: Array<{ value: string; label: string }> = [
  { value: 'open,user_reply', label: 'Open' },
  { value: 'awaiting_user,pending', label: 'Awaiting your reply' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
]
const PRIORITIES: TicketPriority[] = ['high', 'normal', 'low']

// V2 filterbar select (mockup `.filterbar select`): pill radius, 36px,
// hairline border — shared by every select in this filterbar.
const filterSelectClass =
  'h-[var(--etn-control-height-input)] shrink-0 rounded-etn-pill border border-etn-border-input bg-etn-surface px-4 text-etn-control text-etn-text-cell'

// V2 table header cell (mockup `th`): 36px, 11px uppercase letterspaced label.
const thClass =
  'h-9 whitespace-nowrap border-b border-[var(--etn-gray-100)] px-4 text-left text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label'

// V2 table card. overflow-x-auto rather than overflow-hidden: still clips to
// the rounded corners, but a narrow viewport scrolls a wide table instead of
// silently losing its right-hand columns.
const tableCardClass =
  'overflow-x-auto rounded-etn-card border border-etn-border-hairline bg-etn-surface shadow-etn-whisper'

// Customer inbox. The admin queue (filters, search, sort) lives in AdminQueueView.
export function TicketListView({ hrefBase = '/tickets', asUser }: { hrefBase?: string; asUser?: string }) {
  const [status, setStatus] = useState<string>('all')
  const [priority, setPriority] = useState<TicketPriority | 'all'>('all')

  // Debounced search, same shape as AdminQueueView's: local input state, and
  // an applied value that drives the request.
  const [searchText, setSearchText] = useState('')
  const [q, setQ] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setQ(searchText.trim()), 300)
    return () => clearTimeout(t)
  }, [searchText])

  const params: Record<string, unknown> = {}
  if (status !== 'all') params.status = status
  if (priority !== 'all') params.priority = priority
  if (q) params.q = q

  const { data, isLoading, error, refetch } = useTickets(params, asUser)
  const hasFilters = status !== 'all' || priority !== 'all' || Boolean(q)

  // A forged asUser on a non-admin session gets an explicit message, not a
  // blank inbox that reads as "you have no tickets".
  const statusCode = (error as { status?: number; statusCode?: number } | null)?.status
    ?? (error as { statusCode?: number } | null)?.statusCode
  if (statusCode === 403) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        You don&apos;t have access to view as another user.
      </div>
    )
  }

  return (
    <>
      {data?.viewingAs && <ViewingAsBanner name={data.viewingAs.name} companyName={data.viewingAs.companyName} />}
      <div className="flex gap-8">
        <div className="flex-1 min-w-0">
          <div className="mb-4 flex items-center justify-between">
            <h1 className="text-lg font-semibold text-etn-text">Tickets</h1>
            <Link to={'/tickets/new' as never} className="inline-flex">
              <Button>
                <Plus className="h-3.5 w-3.5" />
                New ticket
              </Button>
            </Link>
          </div>

          <div className="mb-5 flex flex-wrap items-center gap-2.5">
            <div className="relative min-w-[200px] flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-etn-text-soft" />
              <Input
                pill
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Search tickets…"
                aria-label="Search tickets"
                className="pl-9"
              />
            </div>
            <select
              aria-label="Status"
              className={filterSelectClass}
              value={status}
              onChange={e => setStatus(e.target.value)}
            >
              <option value="all">All statuses</option>
              {STATUSES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <select
              aria-label="Priority"
              className={filterSelectClass}
              value={priority}
              onChange={e => setPriority(e.target.value as TicketPriority | 'all')}
            >
              <option value="all">All priorities</option>
              {PRIORITIES.map(p => (
                <option key={p} value={p}>{priorityLabels[p]}</option>
              ))}
            </select>
          </div>

          {isLoading && <ListSkeleton rows={5} />}

          {error && (
            <div className="flex flex-col items-center gap-3 py-16">
              <EmptyState
                icon={<AlertTriangle />}
                title="Couldn't load your tickets."
                body="Something went wrong. Please try again."
              />
              <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
            </div>
          )}

          {!isLoading && !error && (data?.data.length ?? 0) === 0 && (
            hasFilters
              ? <EmptyState icon={<Inbox />} title="No tickets match." body="Try widening the filters." />
              : <EmptyState icon={<Inbox />} title="No tickets yet" body="When you open a support ticket it'll show up here." cta={{ label: 'Open a ticket', to: '/tickets/new' }} />
          )}

          {!isLoading && !error && (data?.data.length ?? 0) > 0 && (
            <div className={tableCardClass}>
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th scope="col" className={thClass}>Ticket</th>
                    <th scope="col" className={thClass}>From</th>
                    <th scope="col" className={thClass}>Status</th>
                    <th scope="col" className={thClass}>Priority</th>
                    <th scope="col" className={thClass}>Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-etn-border-row">
                  {data!.data.map((t: Ticket) => (
                    <TicketRow
                      key={t._id}
                      ticket={t}
                      relation={t.viewerRelation ?? 'owner'}
                      hrefBase={hrefBase}
                      asUser={asUser}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
